import { AxiosError } from 'axios'

// Backend error envelope (matches AppError.to_dict() in app/core/errors.py).
export type ApiErrorBody = {
  error: {
    code: string
    message: string
    details?: Record<string, unknown>
  }
}

const FALLBACK_MESSAGE = 'Something went wrong. Please try again.'

function isApiErrorBody(value: unknown): value is ApiErrorBody {
  if (typeof value !== 'object' || value === null) return false
  const maybe = value as { error?: unknown }
  if (typeof maybe.error !== 'object' || maybe.error === null) return false
  const err = maybe.error as { code?: unknown; message?: unknown }
  return typeof err.code === 'string' && typeof err.message === 'string'
}

/**
 * Pull the backend `error.code` out of an axios error, if present. Returns
 * `null` for non-API errors (network, abort, unknown shape).
 */
export function getErrorCode(err: unknown): string | null {
  if (err instanceof AxiosError && isApiErrorBody(err.response?.data)) {
    return err.response.data.error.code
  }
  return null
}

/**
 * Human-readable message for an axios/api error. Prefers the backend-provided
 * message; falls back to a friendly generic for network failures.
 */
export function getErrorMessage(err: unknown): string {
  if (err instanceof AxiosError) {
    if (isApiErrorBody(err.response?.data)) {
      return err.response.data.error.message
    }
    if (err.code === 'ECONNABORTED') {
      return 'Request timed out. Please try again.'
    }
    if (!err.response) {
      return 'Network error. Check your connection and try again.'
    }
    if (err.response.status >= 500) {
      return 'Server error. Please try again in a moment.'
    }
  }
  if (err instanceof Error && err.message) {
    return err.message
  }
  return FALLBACK_MESSAGE
}

export function getErrorStatus(err: unknown): number | null {
  if (err instanceof AxiosError && err.response) {
    return err.response.status
  }
  return null
}

export function getErrorDetails(err: unknown): Record<string, unknown> | null {
  if (err instanceof AxiosError && isApiErrorBody(err.response?.data)) {
    return err.response.data.error.details ?? null
  }
  return null
}
